import { NgRedux, select } from '@angular-redux/store';
import { Component } from '@angular/core';
import { SidenavService } from '../../services/sidenav.service';
import { OrchestrationService } from '../../services/orchestration-service/orchestration.service';
import { AuthDispatcher } from '../../../store/auth/auth-dispatcher';
import { ApiResponseState } from '../../../store/api-response/api-response-state.model';
import { Observable, Subscription } from 'rxjs';
import { ApiResponseActions } from '../../../store/api-response/api-response-actions';
import { AppState } from '../../../store/state.model';
import { TranslationService } from '../../../common/services/languages/translation.service';
import { SnackBarService } from '../../../common/services/snack-bar/snack-bar.service';
import { ConfirmationComponent } from '../../confirmation/confirmation.component';
import { MatSnackBar } from '@angular/material';
import { NgIf, NgFor, KeyValuePipe } from '@angular/common';
import { MatCheckbox } from '@angular/material/checkbox';
import { MatFormField, MatLabel, MatSelect, MatOption } from '@angular/material/select';
import { FormsModule } from '@angular/forms';
import { MatButton } from '@angular/material/button';
import { TranslatePipe } from '@ngx-translate/core';

@Component({
    selector: 'app-give-goodwill',
    templateUrl: './give-goodwill.component.html',
    styleUrls: ['./give-goodwill.component.scss'],
    imports: [NgIf, NgFor, MatCheckbox, MatFormField, MatLabel, MatSelect, FormsModule, MatOption, MatButton, TranslatePipe, KeyValuePipe]
})
export class GiveGoodwillComponent {
  /**
  * @property Property to store the api subscription
  */
  public apiSubscription: Subscription;
  /**
  *@arg apiObservable   observe api variable in redux
  **/
  @select('api') readonly apiObservable: Observable<ApiResponseState>;
  /**
  * @property Property to check whether Goodwill packages found or not
  */
  public goodwillPackagesExists: boolean;
  /**
  * @property Property to store the goodWillSubscriptions received from the API
  */
  public goodWillSubscriptions: any;
  /**
  * @property Property to format the Goodwill Subscriptions to render onto UI
  */
  public formattedSubscriptionsObject: any;
  /**
  * @property Property to store the list of product names
  */
  public productNames: any[];
  /**
  * @property Property to store the selected product terms
  */
  public selectedProductTerms: any;
  /**
  * @property Property to store the vehicle details
  */
  public vehicleDetails: object;
  /**
  * @property Property to store the paid product names
  */
  public paidProductNames: any[];
  /**
  * @property Property to store only the names of Goodwill packages which should be given
  */
  public requiredGoodwillProducts: any[];
  /**
  * @property Property to store neccessary products which are available to give Goodwill
  */
  public requiredGoodwillProductsObject = [];
  /**
  * @property Property to store the selected subscriptions from the UI
  */
  public selectedSubscriptions = [];
  /**
  * @property Property to stores the boolean value of valid Goodwill Reason
  */
  public isGoodwillReasonValid: boolean;
  /**
  * @property Property to stores the boolean value to enable the confirm button
  */
  public enableConfirmButton = false;
  /**
  * @property Property to stores the selectedServicesObject after sorting
  */
  public selectedServicesObject: any;
  /**
   * @property property to store the list of goodwill reason codes
   */
  public goodwillReasonCodes: string[];
  /**
   * @property property to store the goodwill reason code
   */
  public goodwillReasonCode: any;

  constructor(
    private sidenav: SidenavService,
    public orch: OrchestrationService,
    public authDispatcher: AuthDispatcher,
    public reduxState: NgRedux<AppState>,
    public translate: TranslationService,
    private snackBarService: SnackBarService,
    private readonly snackBar: MatSnackBar
  ) {
    this.getGoodwillPackages();
    this.apiSubscription = this.apiObservable.subscribe((api) => {
      if (api.apiAction === ApiResponseActions.SET_GOODWILL_PACKAGES) {
        if (api.apiResponse['type'] === 'success') {
          this.goodWillSubscriptions = api.apiResponse['payload']['subscriptions'];
          this.setupGoodwillPackagesData(this.goodWillSubscriptions);
        } else if (api.apiResponse['type'] === 'error') {
          this.snackBarService.showToast(this.translate.getCustomTranslation('errors.error-ocurred')['value']);
        }
      }
      if (api.apiAction === ApiResponseActions.SUBMIT_GOODWILL_PACKAGES) {
        if (api.apiResponse['type'] === 'success') {
          this.confirmation('add-goodwill-update', true);
          this.cancel();
          this.orch.getVehicleByVin(this.reduxState.getState().selectedVehicle.vehicle);
        } else if (api.apiResponse['type'] === 'error') {
          this.snackBarService.showToast(this.translate.getCustomTranslation('errors.error-ocurred')['value']);
        }
      }
    });
    this.goodwillReasonCodes = ['Billing/Account', 'Customer Service', 'Device/Activation', 'Enrollment',
      'Product/Service (Non-Remote)', 'Remote', 'Telematics System Outage'];
  }

  /**
  * @function getGoodwillPackages calls the getGoodwillPackages end point
  **/
  public getGoodwillPackages() {
    this.vehicleDetails = this.reduxState.getState().selectedVehicle.vehicle;
    if (!!this.vehicleDetails) {
      this.orch.getGoodwillPackages(this.vehicleDetails);
      this.goodwillPackagesExists = true;
    } else {
      this.goodwillPackagesExists = false;
    }
  }

  /**
  * @function setupGoodwillPackagesData sets the Goodwill packages received from the API to render onto UI
  **/
  public setupGoodwillPackagesData(subscriptionsObject) {
    this.formattedSubscriptionsObject = {};
    this.requiredGoodwillProducts = ['Safety Connect', 'Remote Services', 'Remote Connect', 'Enform Remote', 'EV Remote Connect'];
    this.requiredGoodwillProductsObject = subscriptionsObject.filter(product => this.requiredGoodwillProducts.includes(product['productName']));
    this.requiredGoodwillProductsObject.forEach(product => {
      if (!!this.formattedSubscriptionsObject[product.productName]) {
        this.formattedSubscriptionsObject[product.productName].push({
          'type': product.type,
          'term': product.term,
          'termUnit': product.termUnit,
          'packageID': product.packageID,
          'productID': product.productID,
          'productCode': product.productCode,
          'ratePlanID': product.ratePlanID,
          'productName': product.productName,
          'termSelected': false,
          'productSelected': false
        });
      } else {
        this.formattedSubscriptionsObject[product.productName] = [];
        this.formattedSubscriptionsObject[product.productName].push({
          'type': product.type,
          'term': product.term,
          'termUnit': product.termUnit,
          'packageID': product.packageID,
          'productID': product.productID,
          'productCode': product.productCode,
          'ratePlanID': product.ratePlanID,
          'productName': product.productName,
          'termSelected': false,
          'productSelected': false
        });
      }
    }
    );
    this.productNames = Object.keys(this.formattedSubscriptionsObject);
  }

  /**
  * @function sortSelectedIntervals will sort the selected object based on the term
  **/
  public sortSelectedIntervals(selectedServices) {
    const orderArrayByTerm = (selectedServices, term) => selectedServices.sort((a, b) => a[term] - b[term]);
    this.selectedServicesObject = orderArrayByTerm(selectedServices, 'term');
  }

  /**
  * @function getTranslatedTermUnit will return the translated term string
  **/
  public getTranslatedTermUnit(selectedTerm): string {
    if (selectedTerm === 1) {
      return '1 Month';
    } else {
      return selectedTerm + ' Months';
    }
  }

  /**
  * @function onGoodwillReasonSelected gets the goodwill reason from the UI dropdown
  **/
  public onGoodwillReasonSelected(reason) {
    this.goodwillReasonCode = reason;
    this.isGoodwillReasonValid = true;
    this.enableConfirmButton = this.selectedSubscriptions.length > 0 && this.goodwillReasonCode != '';
  }

  /**
  * @function cancel closes the side nav when called
  **/
  public cancel(): void {
    this.goodwillReasonCode = '';
    this.setupGoodwillPackagesData(this.goodWillSubscriptions);
    this.enableConfirmButton = false;
    this.selectedSubscriptions = [];
    this.sidenav.close();
  }

  /**
  * @function onProductSelect method sets the flag of product selected
  **/
  public onProductSelect(selectedService, event): void {
    this.sortSelectedIntervals(selectedService['value']);
    for (const [key] of Object.entries(this.formattedSubscriptionsObject)) {
      if (key === selectedService['key']) {
        this.selectedProductTerms = this.formattedSubscriptionsObject[key];
      }
    }
    this.selectedProductTerms.forEach(element => {
      element['productSelected'] = !element['productSelected'];
    });
    if (!event.checked) {
      this.selectedProductTerms.forEach(element => {
        element['termSelected'] = false;
      });
      this.selectedSubscriptions.forEach((element, index) => {
        if (element['productName'] === selectedService['key']) {
          this.selectedSubscriptions.splice(index, 1);
        }
      });
    }
    this.enableConfirmButton = this.selectedSubscriptions.length > 0 ? true : false;
  }

  /**
  * @function onProductTermSelect method sets the flag of selected term as true
  **/
  public onProductTermSelect(index, trailObject, period, event): void {
    this.selectedSubscriptions.forEach((element, index) => {
      if (element['productCode'] === period.productCode) {
        this.selectedSubscriptions.splice(index, 1);
      }
    });
    if (!!event.checked) {
      trailObject.forEach(element => {
        element['termSelected'] = false;
      });
      trailObject[index]['termSelected'] = true;
      const temp = {
        'packageID': period.packageID,
        'productID': period.productID,
        'productCode': period.productCode,
        'ratePlanID': period.ratePlanID,
        'term': period.term,
        'termUnit': period.termUnit,
        'productName': period.productName,
        'type': period.type,
        'goodwillGrantor': 'CT_PORTAL',
        'goodwillReasonCode': this.goodwillReasonCode,
        'renewable': false
      };
      this.selectedSubscriptions.push(temp);
    }
    if (!event.checked) {
      this.selectedSubscriptions.forEach((element, index) => {
        if (element['packageID'] === period.packageID) {
          this.selectedSubscriptions.splice(index, 1);
        }
      });
    }
    this.enableConfirmButton = this.selectedSubscriptions.length > 0 ? true : false;
  }


  /**
  * @function confirm constructs the payload and hit the submit Goodwill packages API
  **/
  public confirm(): void {
    this.selectedSubscriptions.forEach(element => {
      element['goodwillReasonCode'] = this.goodwillReasonCode;
    });
    const payload = {
      'vin': this.vehicleDetails['vin'],
      'subscriberGuid': this.vehicleDetails['subscriberGuid'],
      'region': this.vehicleDetails['region'],
      'generation': this.vehicleDetails['generation'],
      'waiver': false,
      'createDate': new Date().toISOString(),
      'createSource': 'CT_PORTAL',
      'termsAcceptanceDate': new Date().toISOString(),
      'dataConsent': this.vehicleDetails['dataConsent'],
      'subscriptions': this.selectedSubscriptions
    };
    this.orch.submitGoodwillPackages(payload, this.vehicleDetails);
  }

  /**
  * @function confirmation should call confirmation component when called
  * @param updateValue of type string
  **/
  public confirmation(updateValue: string, success: boolean): void {
    window.scrollTo(0, 0);
    this.snackBar.openFromComponent(ConfirmationComponent, {
      data: {
        type: updateValue,
        success
      },
      verticalPosition: 'top',
      panelClass: ['admin-confirmation'],
    });
  }
}
